import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { LeadDetail, LeadStage, PipelineStage } from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { resolveStageChange } from "@/lib/board-drag";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";

export function useLeadDetail(leadId: string) {
  return useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => api.get<LeadDetail>(`/crm/leads/${leadId}`),
    enabled: !!leadId,
  });
}

export function useUpdateStage(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stage: LeadStage) =>
      api.patch<{ id: string; stage: LeadStage; aiPaused: boolean }>(
        `/crm/leads/${leadId}/stage`,
        { stage }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      toast.success("Etapa actualizada");
    },
  });
}

type StageChange = {
  /** Ask to move the lead. Moves at once, or parks the column in `pendingStage`. */
  requestStage: (toStage: LeadStage) => void;
  /** The `requiresReason` column waiting on StageReasonModal, if any. */
  pendingStage: PipelineStage | null;
  /** The modal saved the reason as a note: now move. */
  confirmPending: () => void;
  cancelPending: () => void;
  isPending: boolean;
};

// The lead detail's stage control, under the same rule as the board
// (`resolveStageChange`): a column flagged `requiresReason` is not entered until
// StageReasonModal has saved the reason, the rest move directly.
export function useStageChange(
  leadId: string,
  currentStage: LeadStage | undefined,
): StageChange {
  const { data: stages } = usePipelineStagesQuery();
  const { mutate: updateStage, isPending } = useUpdateStage(leadId);
  const [pendingStage, setPendingStage] = useState<PipelineStage | null>(null);

  function requestStage(toStage: LeadStage): void {
    if (!currentStage) return;
    const action = resolveStageChange(currentStage, toStage, stages);
    if (action.type === "no-op") return;
    if (action.type === "modal") {
      setPendingStage(stages?.find((s) => s.key === action.toStage) ?? null);
      return;
    }
    updateStage(action.toStage);
  }

  function confirmPending(): void {
    if (!pendingStage) return;
    updateStage(pendingStage.key);
    setPendingStage(null);
  }

  return {
    requestStage,
    pendingStage,
    confirmPending,
    cancelPending: () => setPendingStage(null),
    isPending,
  };
}

export function useToggleBot(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aiPaused: boolean) =>
      api.patch<{ id: string; aiPaused: boolean }>(
        `/crm/leads/${leadId}/bot`,
        { aiPaused }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    },
  });
}
